// A domain name from a client, normalised: lower-case ASCII, an IDN as punycode. Syntax only
// (domain-search D3): which part is the TLD, and whether it is sold, is pricing.resolveTld's
// answer, read from the TLDs staff offer. A bad name fails here, so the route answers 422
// (screaming.md, rule 19).
import { z } from "zod";

export type ParsedDomainName = { name: string; labels: string[] };

/** One DNS label, lower-case ASCII (an IDN label is its punycode). */
export const LABEL = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;

// URL's host parser applies IDNA (UTS #46) in every runtime we ship to: Node, Bun and browsers.
function toAscii(input: string): string | null {
  const trimmed = input.trim().toLowerCase().replace(/\.$/, "");
  if (trimmed === "" || /[\s/?#@:\\]/.test(trimmed)) return null;
  try {
    return new URL(`http://${trimmed}`).hostname;
  } catch {
    return null;
  }
}

export const DomainName = z.string().transform((input, ctx): ParsedDomainName => {
  const name = toAscii(input);
  const labels = name?.split(".") ?? [];
  if (name === null || name.length > 253 || labels.length < 2 || !labels.every((label) => LABEL.test(label))) {
    ctx.addIssue({ code: "custom", message: "invalid_domain" });
    return z.NEVER;
  }
  return { name, labels };
});
